import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import * as css from './Expertise.m.css';
import { Summary } from '../../widgets/Summary';
import { Link } from '@dojo/framework/routing/Link';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import Page from '../../widgets/Page';
import HeroBackground from '../../widgets/HeroBackground';
import Text from '../../widgets/Text';

const heroImageLeft = require('../../assets/images/hero-mockups/hero-expertise-left.png');
const heroImageLeftHighRes = require('../../assets/images/hero-mockups/hero-expertise-left@2x.png');
const heroImageRight = require('../../assets/images/hero-mockups/hero-expertise-right.png');
const heroImageRightHighRes = require('../../assets/images/hero-mockups/hero-expertise-right@2x.png');
const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const analysisImg = require('../../assets/images/illustration/analysis.svg');
const designImg = require('../../assets/images/illustration/design.svg');
const managementImg = require('../../assets/images/illustration/management.svg');
const technologiesImg = require('../../assets/images/logo-illustration/js-technologies-color.svg');

const factory = create();

export const Expertise = factory(function Expertise() {
	return (
		<Page>
			{{
				head: (
					<Head
						title="Top Enterprise Javascript &amp; Web App Development | SitePen"
						description="We are experts in delivering quality enterprise software solutions. Learn more about our approach to implementing sustainable software and building engaged teams."
					/>
				),
				hero: {
					background: <HeroBackground background="level1PurpleTeal" />,
					content: (
						<div classes={[css.hero]}>
							<div classes={[css.heroImageContainer, css.heroImageContainerLeft]}>
								<img
									src={heroImageLeft}
									srcset={heroImageLeftHighRes}
									alt=""
									width={392}
									height={571}
								/>
							</div>
							<ContentWrapper>
								<div classes={[css.heroBlurb]}>
									<Text type="hero-subhead" align="center">
										A Complete Enterprise
									</Text>
									<Text
										type="hero-primary"
										responsive="callout-head"
										align="center"
									>
										Development Team
									</Text>
									<div classes={css.mobileHeroImage} />
									<Text type="body" align="center">
										<p>
											Building open source tools and sustainable web
											applications is challenging. Real experience is
											required. With nearly two decades at the helm of{' '}
											<Link to="servicesCustomSoftwareDevelopment">
												enterprise web development
											</Link>
											, our project teams have created enterprise-grade tools
											and technologies based on our experience developing
											mission-critical applications for companies worldwide.
											The enterprise is our customer. We are in the unique
											position of understanding its constraints while
											simultaneously shaping the standards and future of the
											open web.
										</p>
									</Text>
								</div>
							</ContentWrapper>
							<div classes={[css.heroImageContainer]}>
								<img
									src={heroImageRight}
									srcset={heroImageRightHighRes}
									alt=""
									width={532}
									height={549}
								/>
							</div>
						</div>
					)
				},
				page: (
					<virtual>
						<Section type="leading">
							<ContentWrapper size="smaller">
								<Summary
									trailingBias="medium"
									imageGutter="small"
									paddingBottom="sm"
								>
									{{
										title: 'Web Engineering',
										blurb: (
											<virtual>
												Engineering considerations for the enterprise are
												vast. With an agnostic approach to{' '}
												<Link to="expertiseFullStack">
													full-stack JavaScript development
												</Link>
												, our engineers are adept at quickly understanding
												challenging business and technical requirements to
												advise on the right architecture, technical stack,
												and approach for each customer’s unique business
												objectives and constraints.
											</virtual>
										),
										image: <Image src={engineeringImg} size="md" />
									}}
								</Summary>
								<Summary
									trailingBias="medium"
									reverse
									imageGutter="small"
									paddingBottom="sm"
								>
									{{
										title: 'UI Design | UX Design',
										blurb: (
											<virtual>
												Investment in the{' '}
												<Link to="servicesEnterpriseUX">
													UI and UX design for enterprise applications
												</Link>{' '}
												goes beyond the user’s experience. Having an
												enterprise UX designer on your team increases user
												performance and satisfaction, but it also reduces
												time spent to deploy a software solution and
												minimizes the amount of rework on applications.
											</virtual>
										),
										image: <Image src={designImg} size="md" />
									}}
								</Summary>
								<Summary
									trailingBias="medium"
									imageGutter="small"
									paddingBottom="sm"
								>
									{{
										title: 'Business Analysis',
										blurb:
											'Requirements elicitation, prioritization, documentation and validation are essential for efficiency gains everywhere. Our lean approach to business analysis keeps our project teams focused without setbacks from costly assumptions and uncertainty.',
										image: <Image src={analysisImg} size="md" lazy />
									}}
								</Summary>
								<Summary trailingBias="medium" reverse imageGutter="small">
									{{
										title: 'Project Management',
										blurb:
											'Communication. Transparency. Organization. Our technical project management organizes and aligns resources using agile methodologies to execute your vision, strategy, and objectives, propelling your project through to production and beyond.',
										image: <Image src={managementImg} size="md" lazy />
									}}
								</Summary>
							</ContentWrapper>
						</Section>
						<Section shaded>
							<ContentWrapper size="smaller">
								<Summary stack>
									{{
										title: 'Expertise in the Web’s Leading Frameworks',
										image: <Image src={technologiesImg} size="md" lazy />,
										blurb: (
											<virtual>
												With SitePen as your partner you can be confident
												that you will create well-designed, performant and
												sustainable web applications the right way the first
												time. With{' '}
												<Link to="expertiseJavascript">JavaScript</Link> and{' '}
												<Link to="expertiseTypeScript">TypeScript</Link> as
												our core technologies, we choose and utilize web
												frameworks and the extended JavaScript ecosystem to
												build out solutions that deliver for your
												organization. We are{' '}
												<Link to="about">thought leaders</Link> in
												enterprise application and{' '}
												<Link to="servicesEnterpriseCloudServices">
													cloud platform development services
												</Link>
												, and can provide our technical expertise to you at
												any point in the product and software development
												lifecycles.
											</virtual>
										)
									}}
								</Summary>
							</ContentWrapper>
						</Section>
						<Section>
							<ConnectStrip trailing />
						</Section>
					</virtual>
				)
			}}
		</Page>
	);
});

export default Expertise;
